import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './LoginPage';
import './html5up-theme.css';

const HomePage = () => {
  const { isAuthenticated, isLoading, user, logout, getUserDisplayName } = useAuth();
  const [showLogin, setShowLogin] = useState(false);

  const handleLogin = () => {
    setShowLogin(true);
  };

  const handleLoginSuccess = (user) => {
    setShowLogin(false);
    // LoginPage handles the auth context update via authService
  };

  const handleLoginCancel = () => {
    setShowLogin(false);
  };

  return (
    <>
      {showLogin && (
        <LoginPage
          onLoginSuccess={handleLoginSuccess}
          onCancel={handleLoginCancel}
        />
      )}
      <div id="page-wrapper">
      {/* Header */}
      <header id="header">
        <h1>
          <Link to="/" className="logo-link">
            <img src={`${process.env.PUBLIC_URL}/compass_logo.png?v=3`} alt="VILLA Logo" className="navbar-logo" />
            VILLA Research Platform
          </Link>
        </h1>
        <nav style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.8em',
          flexWrap: 'wrap',
          justifyContent: 'flex-end'
        }}>
          {isAuthenticated ? (
            <>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                color: '#19191a',
                fontFamily: 'Raleway, Helvetica, sans-serif',
                fontSize: '0.7em',
                fontWeight: '600',
                letterSpacing: '0.05em',
                lineHeight: '1.2',
                marginRight: '0.5em'
              }}>
                <span>Welcome, {getUserDisplayName()}</span>
              </div>
              <Link to="/select" className="primary">Evaluate</Link>
              <button onClick={logout} className="button">Logout</button>
            </>
          ) : (
            <>
              {isLoading ? (
                <span style={{ color: '#fff' }}>Checking auth...</span>
              ) : (
                <button onClick={handleLogin} className="primary">
                  Researcher Login
                </button>
              )}
            </>
          )}
        </nav>
      </header>

      {/* Banner */}
      <section id="banner">
        <div className="inner">
          <div className="logo">
            <span className="icon">🧬</span>
          </div>
          <h2>VILLA</h2>
          <p><strong>V</strong>ersatile <strong>I</strong>nformation retrieval from scientific <strong>L</strong>iterature using <strong>L</strong>arge l<strong>A</strong>nguage models</p>
        </div>
      </section>

      {/* Wrapper */}
      <section id="wrapper">
        
        {/* About Section */}
        <section className="wrapper spotlight style1">
          <div className="inner">
            <div className="content">
              <h2 className="major">About This Project</h2>
              <p>
                VILLA (Versatile Information retrieval from scientific Literature using large LAnguage models) 
                is a research platform designed to evaluate the effectiveness of various Large Language Models 
                in extracting zoonotic mutations from scientific literature. Our research focuses on understanding 
                how different AI models perform when tasked with analyzing viral protein sequences and identifying 
                mutations that enable viruses to jump between species.
              </p>
              
              <div style={{ 
                marginTop: '2rem',
                padding: '1.5rem',
                backgroundColor: 'rgba(250, 251, 252, 0.8)',
                borderRadius: '8px',
                borderLeft: '4px solid #bddbf3',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
                flexWrap: 'wrap'
              }}>
                <img 
                  src={`${process.env.PUBLIC_URL}/nsf-compass-pipp-logo.png`}
                  alt="NSF COMPASS PIPP Logo"
                  style={{
                    maxHeight: '100px',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                />
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <p style={{ margin: '0', fontSize: '0.9em', lineHeight: '1.6' }}>
                    This work is supported by the National Science Foundation (NSF) under the Predictive Intelligence for Pandemic Prevention (PIPP) program (Award No. 2412115) through the COMPASS center.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Research Objectives Section */}
        <section className="wrapper alt spotlight style2">
          <div className="inner">
            <div className="content">
              <h2 className="major">Research Objectives</h2>
              <p>
                Our comprehensive study aims to advance the field of computational biology by systematically 
                evaluating AI capabilities in protein sequence analysis. Through rigorous testing and expert 
                evaluation, we seek to understand the current limitations and potential of large language models 
                in biological research applications.
              </p>
              <ul>
                <li>Assess LLM accuracy in protein sequence prediction tasks</li>
                <li>Compare performance across different model architectures</li>
                <li>Evaluate reasoning quality and scientific validity of AI-generated outputs</li>
                <li>Gather expert feedback on model-generated protein sequence predictions</li>
              </ul>
            </div>
          </div>
        </section>


      </section>

      {/* Footer */}
      <section id="footer">
        <div className="inner">
          <ul className="copyright">
            <li>&copy; 2026</li>
            <li>Design: <a href="http://html5up.net">HTML5 UP</a></li>
          </ul>
        </div>
      </section>
      </div>
    </>
  );
};

export default HomePage;
